// src/api/couponIssuance/couponIssuanceApi.ts
// 대량 쿠폰 발급 (CouponIssuanceApiController)
import { axiosInstance } from "src/api/axiosInstance";
import type { ApiResponse } from "src/api/common/apiTypes";
import type { CouponType, DiscountType } from "src/api/coupon/couponApi";

const BASE = "/v1/admin/coupon-issuances";

export type IssueSourceType =
  | "SIGNUP"
  | "PET_INFO"
  | "EVENT"
  | "ORDER"
  | "CHALLENGE"
  | "TRAINING"
  | "SOLUTION"
  | "ADMIN";

export type BulkIssueStatus = "PROCESSING" | "COMPLETED" | "PARTIAL_FAILED";

// 직접 지정 대량 발급
export interface DirectBulkIssuePayload {
  userIds: number[];
  couponIds: number[];
}

export interface IssuedCoupon {
  userCouponId: number;
  couponId: number;
  couponName: string;
  couponCode: string;
  couponType: CouponType;
  discountType: DiscountType;
  discountValue: number;
  issuedAt: string;
  expiredAt: string;
  issueSourceType: IssueSourceType;
}

export interface SkippedCoupon {
  userId: number;
  couponId: number;
  reason: string;
}

export interface DirectBulkIssueResponse {
  successCount: number;
  skippedCount: number;
  issuedCoupons: IssuedCoupon[];
  skippedCoupons: SkippedCoupon[];
}

export const bulkIssueCouponApi = async (
  payload: DirectBulkIssuePayload
): Promise<ApiResponse<DirectBulkIssueResponse>> => {
  const res = await axiosInstance.post<ApiResponse<DirectBulkIssueResponse>>(BASE, payload);
  return res.data;
};

// 필터 기반 대량 발급
export interface FilterIssueCouponPayload {
  couponIds: number[];
  expiredAt?: string;
  filter: {
    joinedFrom?: string;
    joinedTo?: string;
    includeDormant?: boolean;
    lastLoginFrom?: string | null;
    lastLoginTo?: string | null;
    gender?: "MALE" | "FEMALE" | null;
    hasPet?: boolean | null;
    petType?: "dog" | "cat" | null;
    petBreedId?: number | null;
    petWeightMin?: number | null;
    petWeightMax?: number | null;
    purchasedProductIds?: number[];
    purchasedFrom?: string | null;
    purchasedTo?: string | null;
  };
}

export interface CouponPreviewDetail {
  couponId: number;
  couponName: string;
  couponCode: string;
  alreadyIssuedCount: number;
  actualIssueCount: number;
  issueLimit: number;
  currentIssueCount: number;
  issuable: boolean;
}

export interface FilterIssueCouponPreviewResponse {
  totalTargetCount: number;
  couponDetails: CouponPreviewDetail[];
}

export const filterIssueCouponPreviewApi = async (
  payload: FilterIssueCouponPayload
): Promise<ApiResponse<FilterIssueCouponPreviewResponse>> => {
  const res = await axiosInstance.post<ApiResponse<FilterIssueCouponPreviewResponse>>(
    `${BASE}/preview`,
    payload,
    { snackbar: { success: false } }
  );
  return res.data;
};

export interface CouponIssueDetail {
  batchId: string;
  couponId: number;
  couponName: string;
  couponCode: string;
  issuedCount: number;
  skippedCount: number;
}

export interface FilterIssueCouponResponse {
  batchIds: string[];
  totalIssuedCount: number;
  totalSkippedCount: number;
  couponDetails: CouponIssueDetail[];
}

export const filterIssueCouponApi = async (
  payload: FilterIssueCouponPayload
): Promise<ApiResponse<FilterIssueCouponResponse>> => {
  const res = await axiosInstance.post<ApiResponse<FilterIssueCouponResponse>>(
    `${BASE}/filtered`,
    payload
  );
  return res.data;
};

// 배치 이력 목록
export interface BulkIssueHistoryItem {
  batchId: string;
  couponId: number;
  couponName: string;
  couponCode: string;
  issuedCount: number;
  skippedCount: number;
  failedCount: number;
  status: BulkIssueStatus;
  createdId: string;
  createdAt: string;
  completedAt?: string;
}

export interface BulkIssueHistoryResponse {
  content: BulkIssueHistoryItem[];
  page: {
    size: number;
    number: number;
    totalElements: number;
    totalPages: number;
  };
}

export const getBulkIssueHistoryApi = async (params: {
  page: number;
  size: number;
}): Promise<ApiResponse<BulkIssueHistoryResponse>> => {
  const res = await axiosInstance.get<ApiResponse<BulkIssueHistoryResponse>>(BASE, {
    params,
    snackbar: { success: false },
  });
  return res.data;
};

// 배치 상세
export interface BulkIssueDetailItem {
  userCouponId: number;
  userId: number;
  username: string;
  email: string;
  couponStatus: "ISSUED" | "USED" | "EXPIRED";
  issuedAt: string;
  expiredAt: string;
  usedAt?: string;
}

export interface BulkIssueDetailResponse {
  content: BulkIssueDetailItem[];
  page: {
    size: number;
    number: number;
    totalElements: number;
    totalPages: number;
  };
}

export const getBulkIssueDetailApi = async (
  batchId: string,
  params: { page: number; size: number }
): Promise<ApiResponse<BulkIssueDetailResponse>> => {
  const res = await axiosInstance.get<ApiResponse<BulkIssueDetailResponse>>(`${BASE}/${batchId}`, {
    params,
    snackbar: { success: false },
  });
  return res.data;
};
